<template>
  <div class="data-subscriptions">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="brand-header">
          <img 
            src="@/assets/accessiq-logo.png" 
            alt="AccessIQ" 
            class="accessiq-logo-img"
          />
          <span class="brand-subtitle">Data Source Subscriptions</span>
        </div>
        <p class="page-description">
          Manage your regulatory data feeds. Subscribe to official sources to receive
          real-time updates on healthcare policy changes.
        </p>
      </div>
      <div class="header-actions">
        <router-link to="/regulatory-intelligence" class="btn btn-secondary">
          <i class="fas fa-chart-line mr-2"></i>
          View Dashboard
        </router-link>
        <router-link to="/source-catalog" class="btn btn-outline-primary">
          <i class="fas fa-book mr-2"></i>
          Source Catalog
        </router-link>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fas fa-database fa-spin fa-3x"></i>
        <p>Loading data sources...</p>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="content-area">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon subscribed">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ activeSubscriptions.length }}</span>
            <span class="stat-label">Active Subscriptions</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon available">
            <i class="fas fa-globe"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ globalSources.length }}</span>
            <span class="stat-label">Available Sources</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon events">
            <i class="fas fa-file-alt"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ totalEvents.toLocaleString() }}</span>
            <span class="stat-label">Total Events</span>
          </div>
        </div>
      </div>

      <!-- Global Sources Section -->
      <div class="panel">
        <div class="panel-header">
          <h2 class="panel-title">
            <i class="fas fa-landmark mr-2"></i>
            Official Data Sources
          </h2>
          <p class="panel-subtitle">Platform-managed regulatory data feeds</p>
        </div>
        <div class="panel-content">
          <div v-if="globalSources.length === 0" class="empty-state">
            <i class="fas fa-database fa-3x"></i>
            <h3>No Global Sources Available</h3>
            <p>Platform-managed data sources will appear here once configured.</p>
          </div>
          
          <div v-else class="sources-grid">
            <div 
              v-for="source in globalSources" 
              :key="source.id" 
              class="source-card"
              :class="{ 'subscribed': isSubscribed(source.id) }"
            >
              <div class="source-header">
                <span class="source-type-badge" :class="source.source_type">
                  {{ formatSourceType(source.source_type) }}
                </span>
                <span v-if="source.is_active" class="status-badge active">
                  <i class="fas fa-circle"></i> Active
                </span>
                <span v-else class="status-badge inactive">
                  <i class="fas fa-circle"></i> Inactive
                </span>
              </div>
              
              <h3 class="source-name">{{ source.name }}</h3>
              <p class="source-description">{{ source.description }}</p>
              
              <div class="source-stats">
                <div class="stat-item">
                  <i class="fas fa-file-alt"></i>
                  <span>{{ (source.total_events || source.events_count || 0).toLocaleString() }} events</span>
                </div>
                <div class="stat-item" v-if="source.last_updated">
                  <i class="fas fa-clock"></i>
                  <span>Updated {{ formatRelativeTime(source.last_updated) }}</span>
                </div>
              </div>
              
              <div class="source-actions">
                <template v-if="isSubscribed(source.id)">
                  <div class="subscription-info">
                    <span class="subscribed-badge">
                      <i class="fas fa-check"></i> Subscribed
                    </span>
                    <button 
                      @click="showSubscriptionSettings(source)"
                      class="btn-icon"
                      title="Subscription settings"
                    >
                      <i class="fas fa-cog"></i>
                    </button>
                  </div>
                  <button 
                    @click="unsubscribe(source)"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="subscribing"
                  >
                    <i class="fas fa-times mr-1"></i>
                    Unsubscribe
                  </button>
                </template>
                <template v-else>
                  <button 
                    @click="subscribe(source)"
                    class="btn btn-primary btn-full"
                    :disabled="subscribing"
                  >
                    <i class="fas fa-plus mr-1"></i>
                    Subscribe
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Subscriptions Table -->
      <div v-if="activeSubscriptions.length > 0" class="panel subscriptions-panel">
        <div class="panel-header">
          <h2 class="panel-title">
            <i class="fas fa-rss mr-2"></i>
            Your Active Subscriptions
          </h2>
        </div>
        <div class="panel-content">
          <table class="subscriptions-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Filters</th>
                <th>Notifications</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sub in activeSubscriptions" :key="sub.id">
                <td class="source-cell">
                  <span class="source-name">{{ sub.source_name }}</span>
                  <span class="source-type">{{ formatSourceType(sub.source_type) }}</span>
                </td>
                <td class="filters-cell">
                  <div class="filter-tags">
                    <span v-if="sub.state_filter?.length" class="filter-tag">
                      <i class="fas fa-map-marker-alt"></i>
                      {{ sub.state_filter.length }} states
                    </span>
                    <span v-if="sub.severity_filter?.length" class="filter-tag">
                      <i class="fas fa-exclamation-triangle"></i>
                      {{ sub.severity_filter.join(', ') }}
                    </span>
                    <span v-if="!sub.state_filter?.length && !sub.severity_filter?.length" class="filter-tag all">
                      All events
                    </span>
                  </div>
                </td>
                <td class="notifications-cell">
                  <div class="notification-toggles">
                    <span 
                      v-if="sub.notify_on_high_impact" 
                      class="notification-badge" 
                      title="High impact alerts"
                    >
                      <i class="fas fa-bell"></i>
                    </span>
                    <span 
                      v-if="sub.notify_on_effective_soon" 
                      class="notification-badge" 
                      title="Upcoming effective date alerts"
                    >
                      <i class="fas fa-calendar-check"></i>
                    </span>
                    <span v-if="!sub.notify_on_high_impact && !sub.notify_on_effective_soon" class="text-muted">
                      None
                    </span>
                  </div>
                </td>
                <td>
                  <span class="status-pill" :class="sub.is_active ? 'active' : 'paused'">
                    {{ sub.is_active ? 'Active' : 'Paused' }}
                  </span>
                </td>
                <td class="actions-cell">
                  <button 
                    v-if="sub.is_active"
                    @click="pauseSubscription(sub)"
                    class="btn-icon" 
                    title="Pause subscription"
                  >
                    <i class="fas fa-pause"></i>
                  </button>
                  <button 
                    v-else
                    @click="resumeSubscription(sub)"
                    class="btn-icon" 
                    title="Resume subscription"
                  >
                    <i class="fas fa-play"></i>
                  </button>
                  <button 
                    @click="showSubscriptionSettingsById(sub)"
                    class="btn-icon" 
                    title="Edit settings"
                  >
                    <i class="fas fa-cog"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Subscription Settings Modal -->
    <div v-if="showSettingsModal" class="modal-overlay" @click.self="showSettingsModal = false">
      <div class="modal-content settings-modal">
        <div class="modal-header">
          <h2>
            <i class="fas fa-cog mr-2"></i>
            Subscription Settings
          </h2>
          <button @click="showSettingsModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="setting-section">
            <label>Source</label>
            <div class="source-display">
              <i class="fas fa-landmark"></i>
              {{ selectedSubscription?.source_name }}
            </div>
          </div>

          <div class="setting-section">
            <label>State Filters</label>
            <p class="help-text">Leave empty to receive events from all states</p>
            <select v-model="subscriptionSettings.state_filter" multiple class="filter-select">
              <option v-for="state in availableStates" :key="state.code" :value="state.code">
                {{ state.name }}
              </option>
            </select>
          </div>

          <div class="setting-section">
            <label>Severity Filters</label>
            <div class="checkbox-group">
              <label class="checkbox-item">
                <input type="checkbox" v-model="subscriptionSettings.severity_filter" value="high">
                <span class="severity-badge high">High</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="subscriptionSettings.severity_filter" value="medium">
                <span class="severity-badge medium">Medium</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="subscriptionSettings.severity_filter" value="low">
                <span class="severity-badge low">Low</span>
              </label>
            </div>
          </div>

          <div class="setting-section">
            <label>Notifications</label>
            <div class="checkbox-group vertical">
              <label class="checkbox-item">
                <input type="checkbox" v-model="subscriptionSettings.notify_on_high_impact">
                <span>Alert on high-impact changes</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="subscriptionSettings.notify_on_effective_soon">
                <span>Alert when effective date is approaching</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showSettingsModal = false" class="btn btn-secondary">
            Cancel
          </button>
          <button @click="saveSubscriptionSettings" class="btn btn-primary" :disabled="saving">
            <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'DataSubscriptions',

  data() {
    return {
      loading: true,
      subscribing: false,
      saving: false,
      globalSources: [],
      activeSubscriptions: [],
      showSettingsModal: false,
      selectedSubscription: null,
      subscriptionSettings: {
        state_filter: [],
        severity_filter: [],
        notify_on_high_impact: true,
        notify_on_effective_soon: true
      },
      availableStates: [
        { code: 'AL', name: 'Alabama' },
        { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' },
        { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' },
        { code: 'DE', name: 'Delaware' },
        { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' },
        { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' },
        { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' },
        { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' },
        { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' },
        { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' },
        { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' },
        { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' },
        { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' },
        { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' },
        { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' },
        { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' },
        { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' },
        { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' },
        { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' }
      ]
    };
  },

  computed: {
    totalEvents() {
      if (!Array.isArray(this.globalSources)) return 0;
      return this.globalSources.reduce((sum, s) => sum + (s.total_events || s.events_count || 0), 0);
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadGlobalSources(),
          this.loadSubscriptions()
        ]);
      } catch (error) {
        console.error('Failed to load data:', error);
        this.$toast?.error?.('Failed to load data sources');
      } finally {
        this.loading = false;
      }
    },

    async loadGlobalSources() {
      try {
        const response = await api.get('/regulatory/global-sources/catalog/');
        // Catalog returns { sources: [...], total_sources, subscribed_count }
        this.globalSources = response.data?.sources || response.data?.results || [];
      } catch (error) {
        console.error('Failed to load global sources:', error);
        // Try the base endpoint as fallback
        try {
          const response = await api.get('/regulatory/global-sources/');
          this.globalSources = Array.isArray(response.data) 
            ? response.data 
            : (response.data?.results || []);
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          this.globalSources = [];
        }
      }
    },

    async loadSubscriptions() {
      try {
        const response = await api.get('/regulatory/subscriptions/');
        this.activeSubscriptions = response.data.results || response.data || [];
      } catch (error) {
        console.error('Failed to load subscriptions:', error);
        this.activeSubscriptions = [];
      }
    },

    isSubscribed(sourceId) {
      return this.activeSubscriptions.some(s => s.global_source === sourceId);
    },

    getSubscription(sourceId) {
      return this.activeSubscriptions.find(s => s.global_source === sourceId);
    },

    async subscribe(source) {
      this.subscribing = true;
      try {
        await api.post(`/regulatory/global-sources/${source.id}/subscribe/`);
        this.$toast?.success?.(`Subscribed to ${source.name}`);
        await this.loadSubscriptions();
      } catch (error) {
        console.error('Failed to subscribe:', error);
        this.$toast?.error?.('Failed to subscribe to source');
      } finally {
        this.subscribing = false;
      }
    },

    async unsubscribe(source) {
      const subscription = this.getSubscription(source.id);
      if (!subscription) return;

      if (!confirm(`Are you sure you want to unsubscribe from ${source.name}?`)) return;

      try {
        await api.delete(`/regulatory/subscriptions/${subscription.id}/`);
        this.$toast?.success?.(`Unsubscribed from ${source.name}`);
        await this.loadSubscriptions();
      } catch (error) {
        console.error('Failed to unsubscribe:', error);
        this.$toast?.error?.('Failed to unsubscribe');
      }
    },

    async pauseSubscription(subscription) {
      try {
        await api.post(`/regulatory/subscriptions/${subscription.id}/pause/`);
        this.$toast?.success?.('Subscription paused');
        await this.loadSubscriptions();
      } catch (error) {
        console.error('Failed to pause subscription:', error);
        this.$toast?.error?.('Failed to pause subscription');
      }
    },

    async resumeSubscription(subscription) {
      try {
        await api.post(`/regulatory/subscriptions/${subscription.id}/resume/`);
        this.$toast?.success?.('Subscription resumed');
        await this.loadSubscriptions();
      } catch (error) {
        console.error('Failed to resume subscription:', error);
        this.$toast?.error?.('Failed to resume subscription');
      }
    },

    showSubscriptionSettings(source) {
      const subscription = this.getSubscription(source.id);
      if (!subscription) return;
      this.showSubscriptionSettingsById(subscription);
    },

    showSubscriptionSettingsById(subscription) {
      this.selectedSubscription = subscription;
      this.subscriptionSettings = {
        state_filter: subscription.state_filter || [],
        severity_filter: subscription.severity_filter || [],
        notify_on_high_impact: subscription.notify_on_high_impact ?? true,
        notify_on_effective_soon: subscription.notify_on_effective_soon ?? true
      };
      this.showSettingsModal = true;
    },

    async saveSubscriptionSettings() {
      if (!this.selectedSubscription) return;

      this.saving = true;
      try {
        await api.patch(`/regulatory/subscriptions/${this.selectedSubscription.id}/`, {
          state_filter: this.subscriptionSettings.state_filter,
          severity_filter: this.subscriptionSettings.severity_filter,
          notify_on_high_impact: this.subscriptionSettings.notify_on_high_impact,
          notify_on_effective_soon: this.subscriptionSettings.notify_on_effective_soon
        });
        this.$toast?.success?.('Settings saved');
        this.showSettingsModal = false;
        await this.loadSubscriptions();
      } catch (error) {
        console.error('Failed to save settings:', error);
        this.$toast?.error?.('Failed to save settings');
      } finally {
        this.saving = false;
      }
    },

    formatSourceType(type) {
      const types = {
        'federal_register': 'Federal Register',
        'cms_medicaid': 'CMS Medicaid',
        'regulations_gov': 'Regulations.gov',
        'state_medicaid': 'State Medicaid'
      };
      return types[type] || type;
    },

    formatRelativeTime(dateStr) {
      if (!dateStr) return 'Never';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.data-subscriptions {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8fafc;
  min-height: 100vh;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.accessiq-logo-img {
  height: 40px;
  width: auto;
}

.brand-subtitle {
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
}

.page-description {
  color: #64748b;
  margin: 0;
  max-width: 600px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
  color: #64748b;
}

.loading-spinner i {
  color: #0d9488;
  margin-bottom: 16px;
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.stat-icon.subscribed {
  background: #d1fae5;
  color: #059669;
}

.stat-icon.available {
  background: #dbeafe;
  color: #2563eb;
}

.stat-icon.events {
  background: #fef3c7;
  color: #d97706;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
}

/* Panels */
.panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.panel-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.panel-title i {
  color: #64748b;
}

.panel-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.panel-content {
  padding: 24px;
}

/* Sources Grid */
.sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.source-card {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
}

.source-card:hover {
  border-color: #cbd5e1;
}

.source-card.subscribed {
  background: #f0fdf4;
  border-color: #86efac;
}

.source-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.source-type-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: uppercase;
}

.source-type-badge.federal_register {
  background: #dbeafe;
  color: #1e40af;
}

.source-type-badge.cms_medicaid {
  background: #d1fae5;
  color: #047857;
}

.source-type-badge.regulations_gov {
  background: #fef3c7;
  color: #92400e;
}

.status-badge {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-badge.active {
  color: #059669;
}

.status-badge.active i {
  font-size: 6px;
}

.status-badge.inactive {
  color: #94a3b8;
}

.source-name {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.source-description {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.source-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.stat-item {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-item i {
  color: #94a3b8;
}

.source-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subscription-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subscribed-badge {
  font-size: 13px;
  font-weight: 500;
  color: #059669;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Subscriptions Table */
.subscriptions-table {
  width: 100%;
  border-collapse: collapse;
}

.subscriptions-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0;
}

.subscriptions-table td {
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.subscriptions-table tr:hover {
  background: #f8fafc;
}

.source-cell .source-name {
  display: block;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  font-size: 14px;
}

.source-cell .source-type {
  font-size: 11px;
  color: #64748b;
}

.filter-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-tag {
  font-size: 11px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-tag.all {
  background: #d1fae5;
  color: #047857;
}

.notification-toggles {
  display: flex;
  gap: 8px;
}

.notification-badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}

.status-pill.active {
  background: #d1fae5;
  color: #047857;
}

.status-pill.paused {
  background: #fef3c7;
  color: #92400e;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: all 0.2s;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-full {
  width: 100%;
}

.btn-primary {
  background: #0d9488;
  color: white;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-outline-primary {
  background: white;
  color: #0d9488;
  border: 1px solid #0d9488;
}

.btn-outline-primary:hover {
  background: #f0fdfa;
}

.btn-outline-danger {
  background: white;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.btn-outline-danger:hover {
  background: #fef2f2;
  border-color: #dc2626;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  border-color: #0d9488;
  color: #0d9488;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 18px;
  margin: 0;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
}

.close-btn:hover {
  color: #0f172a;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.setting-section {
  margin-bottom: 24px;
}

.setting-section label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.help-text {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 8px 0;
}

.source-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-weight: 500;
  color: #0f172a;
}

.source-display i {
  color: #0d9488;
}

.filter-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.checkbox-group.vertical {
  flex-direction: column;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-item input {
  cursor: pointer;
}

.severity-badge {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
}

.severity-badge.high {
  background: #fef2f2;
  color: #dc2626;
}

.severity-badge.medium {
  background: #fef3c7;
  color: #d97706;
}

.severity-badge.low {
  background: #d1fae5;
  color: #059669;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
}

.empty-state i {
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: #334155;
  margin: 0 0 8px 0;
}

.empty-state p {
  margin: 0;
}

.text-muted {
  color: #94a3b8;
  font-size: 12px;
}

/* Responsive */
@media (max-width: 1024px) {
  .stats-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .sources-grid {
    grid-template-columns: 1fr;
  }

  .subscriptions-table {
    display: block;
    overflow-x: auto;
  }
}
</style>
